import type {
  AssetCategory,
  AssetDefinition,
  ResolvedAsset,
  ThemeAssetResolver,
  ThemeManifest,
} from '../../sdk/theme';
import type { AssetId } from '../../shared/types/ids';
import { assetIdPrefix } from '../../shared/validation/asset-id';

/**
 * The platform owns asset resolution; a module only supplies the manifest (module spec §15).
 *
 * Resolution order for an unknown ID (D-014 — never an error, never a blank):
 *   1. the manifest's `categoryPlaceholders` entry for the ID's leading segment,
 *   2. the manifest's `categoryPlaceholders.ui` entry as a generic stand-in,
 *   3. the platform's own built-in placeholder, which always exists.
 */

const ASSET_CATEGORIES: readonly AssetCategory[] = [
  'hero',
  'guide',
  'boss',
  'creature',
  'ar',
  'portal',
  'target',
  'collectible',
  'environment',
  'ui',
  'effect',
  'audio',
];

const CATEGORY_BY_NAME = new Map<string, AssetCategory>(
  ASSET_CATEGORIES.map((category) => [category, category]),
);

/** Shipped with the platform so resolution can never dead-end (public/assets/platform). */
export const PLATFORM_PLACEHOLDER_ASSET: AssetDefinition = {
  id: 'platform.placeholder.v1',
  source: '/assets/platform/placeholder.svg',
  type: 'svg',
  category: 'ui',
  version: 1,
  format: 'svg',
  transparent: true,
  approvalStatus: 'draft',
  preload: false,
  width: 200,
  height: 200,
};

export interface ThemeAssetResolverOptions {
  /**
   * Called whenever an ID falls back, so the shell can surface placeholder usage during
   * development instead of it passing silently.
   */
  onFallback?: (requestedAssetId: AssetId, used: AssetDefinition) => void;
}

/**
 * `assetId` on the result is always the ID that was asked for; `definition.id` is what was
 * actually used. They differ exactly when `usedFallback` is true.
 */
export function createThemeAssetResolver(
  manifest: ThemeManifest,
  options: ThemeAssetResolverOptions = {},
): ThemeAssetResolver {
  const definitions = new Map<AssetId, AssetDefinition>();
  for (const [assetId, definition] of Object.entries(manifest.assets)) {
    definitions.set(assetId, definition);
  }
  for (const [assetId, definition] of Object.entries(manifest.ui)) {
    definitions.set(assetId, definition);
  }

  const categoryOf = (assetId: AssetId): AssetCategory | undefined => {
    const prefix = assetIdPrefix(assetId);
    if (prefix === null) return undefined;
    return CATEGORY_BY_NAME.get(prefix);
  };

  const placeholdersFor = (assetId: AssetId): AssetDefinition[] => {
    const category = categoryOf(assetId);
    const candidates: (AssetId | undefined)[] = [
      category === undefined ? undefined : manifest.categoryPlaceholders[category],
      manifest.categoryPlaceholders.ui,
    ];

    const found: AssetDefinition[] = [];
    for (const candidate of candidates) {
      if (candidate === undefined) continue;
      const definition = definitions.get(candidate);
      if (definition !== undefined && !found.includes(definition)) found.push(definition);
    }

    found.push(PLATFORM_PLACEHOLDER_ASSET);
    return found;
  };

  /**
   * Ordered candidates for an ID. A declared asset comes first, then whatever it nominates as
   * its own fallback, then the placeholders — so a *known* ID whose file is missing still ends
   * somewhere safe rather than rendering nothing.
   */
  const chainFor = (assetId: AssetId): AssetDefinition[] => {
    const chain: AssetDefinition[] = [];
    const seen = new Set<AssetId>();

    let current = definitions.get(assetId);
    // `fallbackAssetId` may point at another declared asset that also nominates a fallback;
    // `seen` stops a manifest cycle from looping forever.
    while (current !== undefined && !seen.has(current.id)) {
      seen.add(current.id);
      chain.push(current);
      const next = current.fallbackAssetId;
      current = next === undefined ? undefined : definitions.get(next);
    }

    for (const placeholder of placeholdersFor(assetId)) {
      if (seen.has(placeholder.id)) continue;
      seen.add(placeholder.id);
      chain.push(placeholder);
    }

    return chain;
  };

  return {
    has(assetId: AssetId): boolean {
      return definitions.has(assetId);
    },

    resolve(assetId: AssetId): ResolvedAsset {
      const direct = definitions.get(assetId);
      if (direct !== undefined) {
        return { assetId, definition: direct, usedFallback: false };
      }

      // The manifest never declared this ID at all, which is exactly when a placeholder is
      // required. A declared-but-unloadable asset is handled by resolveChain instead.
      const [placeholder] = placeholdersFor(assetId);
      const used = placeholder ?? PLATFORM_PLACEHOLDER_ASSET;
      options.onFallback?.(assetId, used);
      return { assetId, definition: used, usedFallback: true };
    },

    resolveChain(assetId: AssetId): ResolvedAsset[] {
      const declared = definitions.has(assetId);
      return chainFor(assetId).map((definition, index) => ({
        assetId,
        definition,
        usedFallback: !(declared && index === 0),
      }));
    },
  };
}
